import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import { BASE_URL } from "../../../const";

const URL = `${BASE_URL}/plant-wise-task-agg`;

export default function PlantWiseTaskAggPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { layer } = useParams();
  const navigate = useNavigate();

  const [warehouse, setWarehouse] = useState(undefined);

  // Resolve warehouse: selectedWarehouse -> map store -> undefined
  useEffect(() => {
    const selected = localStorage.getItem("selectedWarehouse");
    if (selected) {
      setWarehouse(selected);
      return;
    }

    const store = localStorage.getItem("store");
    if (store === "SELECT BRANDS (I) PVT. LTD. (PUNE)") {
      setWarehouse("pune");
    } else if (store === "SELECT BRANDS (I) PVT. LTD. (KOLKATA)") {
      setWarehouse("kolkata");
    } else {
      // neither selectedWarehouse nor known store mapping -> keep undefined
      setWarehouse(undefined);
    }
  }, []);

  const fetchData = async (wh) => {
    if (!wh) return; // guard: do not fetch without a resolved warehouse
    try {
      setLoading(true);
      const token = localStorage.getItem("token") || "";
      const res = await fetch(
        `${URL}?layer=${encodeURIComponent(layer)}&warehouse=${encodeURIComponent(wh)}`,
        {
          headers: {
            Authorization: token ? `Token ${token}` : "",
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.error("API error fetching plant data:", res.status, txt);
        setData([]);
        return;
      }

      const json = await res.json();
      const items = Array.isArray(json) ? json : json.data || json.results || json.items || [];
      setData(items);
    } catch (err) {
      console.error("Error fetching plant data:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch when layer OR warehouse changes — only when warehouse is defined
  useEffect(() => {
    if (layer && warehouse) {
      fetchData(warehouse);
    } else {
      // if no warehouse resolved, stop loading and clear data
      if (!warehouse) {
        setLoading(false);
        setData([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, warehouse]);

  const handlePlantClick = (plantSku, plantName) => {
    const params = new URLSearchParams({
      layer: layer || "",
      sku: plantSku || "",
      plantName: plantName || "",
    });
    navigate(`/tasks/filter?${params.toString()}`);
  };

  return (
    <>
      <Navbar />
      <div className="p-4 max-w-7xl mx-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/task-section")}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition border border-gray-300"
                title="Back"
              >
                <svg className="w-4 h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div>
                <h2 className="text-xl font-bold text-gray-900">Plant-wise Task Summary</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-600">Layer</span>
                  <span className="bg-blue-100 text-blue-800 text-s font-semibold px-2 py-1 rounded-md border border-blue-200">
                    {layer}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">Warehouse: {warehouse ?? "not selected"}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => fetchData(warehouse)}
              className="bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 active:bg-gray-800 transition flex items-center justify-center"
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Loading / Empty / Content */}
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Loading plant data...</p>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-6 text-center border border-gray-200">
            <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m0 0V9m0 8h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-600 mt-3">
              {warehouse ? "No data found." : "No warehouse selected — set selectedWarehouse or ensure store maps to a warehouse."}
            </p>
          </div>
        ) : (
          <div
            className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-y-auto"
            style={{ height: "calc(100vh - 200px)", maxHeight: "60vh" }}
          >
            {/* Column Headers */}
            <div className="bg-gray-100 rounded-t-xl p-3 grid grid-cols-12 gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wide sticky top-0 z-10">
              <div className="col-span-5">Plant Details</div>
              <div className="col-span-3 text-center">Pending Tasks</div>
              <div className="col-span-3 text-center">Qty Pending</div>
              <div className="col-span-1"></div>
            </div>

            {/* Plant Cards */}
            <div className="space-y-2 mt-2 p-3">
              {data.map((plant, idx) => (
                <div
                  key={plant.plant_sku ?? idx}
                  onClick={() => handlePlantClick(plant.plant_sku, plant.plant_name)}
                  className="group cursor-pointer bg-white border border-gray-200 rounded-lg hover:shadow-md hover:border-blue-400 active:border-blue-500 transition-all duration-200 p-3"
                >
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      <div className="mb-1">
                        <div className="text-sm font-semibold text-gray-900 line-clamp-2 leading-tight">
                          {plant.plant_name}
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 font-medium">{plant.plant_sku}</div>
                    </div>

                    <div className="col-span-3 flex justify-center">
                      <div className="text-center">
                        <div className="text-lg font-bold text-orange-600">{plant.pending_task_count ?? 0}</div>
                      </div>
                    </div>

                    <div className="col-span-3 flex justify-center">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        </div>
                        <div className="text-lg font-bold text-red-600">
                          {(plant.total_qty_pending ?? 0).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="col-span-1 flex justify-end">
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
}
